import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  Cell,
} from "recharts";
import { useChartTheme } from "../charts/hooks/useChartTheme";
import { CustomTooltip } from "../charts/custom-tooltip";
import { CustomLegend } from "../charts/custom-legend";
import { ChartWrapper } from "../charts/chart-wrapper";
import type { FC } from "react";

interface StageData {
  readonly stageName: string;
  readonly weightedRevenue: number;
}

interface Props {
  readonly data: readonly StageData[];
}

/**
 * Renders a bar chart showing Weighted Revenue by Stage for leads
 */
export const LeadsWeightedRevenueByStageChart: FC<Props> = ({ data }) => {
  const { isDark, chartProps } = useChartTheme();
  const barColor = "#6366F1"; // Indigo

  return (
    <ChartWrapper height={400}>
      <BarChart
        data={[...data]}
        margin={{ top: 20, right: 40, left: 80, bottom: 10 }}
      >
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis
          dataKey="stageName"
          angle={-45}
          textAnchor="end"
          height={80}
          interval={0}
          tick={{ fontSize: 12 }}
        />
        <YAxis
          width={75}
          tick={{ fontSize: 12 }}
          label={{
            value: "Weighted Revenue ($)",
            angle: -90,
            position: "insideLeft",
            offset: -60,
            fontSize: 14,
            style: { textAnchor: "middle" },
          }}
          tickFormatter={(value) => `$${(value / 1000).toFixed(0)}k`}
        />
        <Tooltip
          content={<CustomTooltip />}
          cursor={false}
          {...chartProps.tooltip}
          formatter={(value: number) => [
            `$${value.toLocaleString()}`,
            "Weighted Revenue",
          ]}
          labelFormatter={(label: string) => `Stage: ${label}`}
        />
        <Legend
          content={<CustomLegend />}
          {...chartProps.legend}
          verticalAlign="bottom"
          height={36}
        />
        <Bar
          dataKey="weightedRevenue"
          fill={barColor}
          name="Weighted Revenue"
          radius={[4, 4, 0, 0]}
          maxBarSize={50}
        >
          {[...data].map((_, index) => (
            <Cell key={`cell-${index}`} fill={barColor} />
          ))}
        </Bar>
      </BarChart>
    </ChartWrapper>
  );
};
